<template>
  <ul :class="['language', { dark: isDark }]">
    <li>
      <font-awesome-icon icon="language" size="lg" />
    </li>
    <li v-for="option in locales" :key="option.code">
      <!-- A link to the same page in that language. The signed preview has no
           such URL (the path is the signature), so it keeps the in-place swap. -->
      <Link v-if="hrefFor(option.code)" :href="hrefFor(option.code)">{{ option.shortName }}</Link>
      <button v-else type="button" @click="setLocale(option.code)">{{ option.shortName }}</button>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The language rail: the fixed switcher down the right edge of the page.

   This is the only language control from 768px up. Below that it is hidden and
   the collapsed header menu carries the switcher as one of its own nav rows,
   because a rotated rail has no room on a phone. The two are not variants of
   each other any more: the rail is this component, the row is Header's, and
   they share the list and the switch action from i18n/locales.js rather than
   each spelling the three languages out. */
.language {
  /* The gap either side of a separator. */
  --sep-gap: 0.5rem;

  align-items: center;
  color: $ground-dark;
  display: flex;
  font-weight: 700;
  position: fixed;
  /* Clears the display cutout when the page is painted edge to edge. It is zero
     on every device without an inset, so the rail does not move. */
  right: calc(1rem + env(safe-area-inset-right, 0px));
  top: 50%;
  transform-origin: right top;
  transform: rotate(-90deg) translate(50%, -100%);
  z-index: 10;

  &.dark {
    color: $on-dark-strong;
  }

  li {
    &:first-child,
    &:not(:last-child) {
      &::after {
        content: '/';
        display: inline-block;
        margin-right: var(--sep-gap);
        padding-left: var(--sep-gap);
      }
    }

    &:first-child {
      &::after {
        content: '-';
      }
    }
  }

  /* The rail's controls are bare text, about a 17px target. A link and the
     preview's button look the same: the link is the control everywhere the URL
     can change, and the button is only the signed preview. A coarse pointer
     gets one worth tapping; a mouse keeps the tight spacing the rotated rail
     was drawn with, so nothing repaints on the desktop. */
  a,
  button {
    background: none;
    border: 0;
    color: inherit;
    font: inherit;
    padding: 0;
    text-decoration: none;
  }

  @media (pointer: coarse) {
    a,
    button {
      padding: 0.65rem 0.8rem;
    }
  }

  @media (max-width: 767.98px) {
    display: none;
  }
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import { useSurface } from '@/composables/useSurface';
import { LOCALES, localeHref, switchLocale } from '@/i18n/locales';

export default {
  components: {
    Link
  },

  methods: {
    handleScroll: _.debounce(function () {
      this.syncSurface();
    }, 50),

    setLocale(code) {
      switchLocale(this, code);
    },

    hrefFor(code) {
      return localeHref(code);
    }
  },

  created() {
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  mounted() {
    this.handleScroll();
  },

  setup() {
    const { isDark, syncSurface } = useSurface();

    return { isDark, syncSurface, locales: LOCALES };
  }
};
</script>
